import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Modal, message, Row, Col, Descriptions, Image, Space, Typography } from 'antd';
import { EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, CopyOutlined, KeyOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../stores/authStore';

// 配置axios默认URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;

const getStatusColor = (status) => {
  switch (status) {
    case 'paid': return 'green';
    case 'pending': return 'orange';
    case 'cancelled': return 'red';
    case 'refunded': return 'blue';
    default: return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'paid': return '已支付';
    case 'pending': return '待支付';
    case 'cancelled': return '已取消';
    case 'refunded': return '已退款';
    default: return '未知';
  }
};

const MyOrders = () => {
  const { user, token } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [orderDetailVisible, setOrderDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [pagination.current, pagination.pageSize]);

  const fetchOrders = async () => {
    if (!token) {
      message.error('请先登录');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get('/card-sales/orders', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'User-ID': user?.id,
          'User-Role': user?.role
        }
      });

      if (response.data.success) {
        setOrders(response.data.orders);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
    } catch (error) {
      console.error('获取订单列表失败:', error);
      if (error.response?.status === 401) {
        message.error('请先登录');
      } else {
        message.error('获取订单列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleViewDetail = async (orderId) => {
    try {
      const response = await axios.get(`/api/card-sales/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'User-ID': user?.id,
          'User-Role': user?.role
        }
      });
      if (response.data.success) {
        setCurrentOrder(response.data.order);
        setOrderDetailVisible(true);
      }
    } catch (error) {
      console.error('获取订单详情失败:', error);
      if (error.response?.status === 401) {
        message.error('请先登录');
      } else {
        message.error('获取订单详情失败');
      }
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'pending': { color: 'orange', text: '待支付' },
      'paid': { color: 'green', text: '已支付' },
      'cancelled': { color: 'red', text: '已取消' },
      'refunded': { color: 'blue', text: '已退款' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getCardTypeName = (cardType) => {
    const typeMap = {
      'basic': '基础会员',
      'vip': 'VIP会员',
      'premium': '高级会员'
    };
    return typeMap[cardType] || cardType;
  };

  const getPaymentMethodName = (method) => {
    const methodMap = {
      'wechat': '微信支付',
      'alipay': '支付宝'
    };
    return methodMap[method] || method;
  };

  // 复制卡密功能
  const copyCardCode = async (cardCode) => {
    try {
      await navigator.clipboard.writeText(cardCode);
      message.success('卡密已复制到剪贴板');
    } catch (error) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = cardCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('卡密已复制到剪贴板');
    }
  };

  const columns = [
    {
      title: '卡密',
      dataIndex: 'cards',
      key: 'cards',
      width: 200,
      render: (cards, record) => {
        if (!cards || cards.length === 0) {
          return <Text type="secondary">暂无卡密</Text>;
        }
        return (
          <Space direction="vertical" size="small">
            {cards.map((card, index) => (
              <Space key={index}>
                <Text code style={{ fontSize: '12px' }}>{card.code}</Text>
                <Button 
                  type="text" 
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => copyCardCode(card.code)}
                  title="复制卡密"
                />
              </Space>
            ))}
          </Space>
        );
      }
    },
    {
      title: '卡密类型',
      dataIndex: 'card_type',
      key: 'card_type',
      width: 100,
      render: (cardType) => getCardTypeName(cardType)
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80
    },
    {
      title: '总金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      render: (amount) => `¥${amount}`
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 100,
      render: (method) => getPaymentMethodName(method)
    },
    {
      title: '状态',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time) => new Date(time).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
        >
          查看详情
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '6px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card 
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>📋 我的订单</Title>
            {/* 激活卡密入口已移除：支付成功自动开通VIP */}
          </Space>
        } 
        style={{ 
          marginBottom: '20px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: 'none'
        }}
        bodyStyle={{ padding: '6px' }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>加载中...</Text>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ShoppingCartOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#D9D9D9' }}>暂无订单记录</Title>
            <Text type="secondary">您还没有任何订单</Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {orders.map((order) => (
              <Col xs={24} sm={12} md={8} lg={6} key={order.id}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '6px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  actions={[
                    <Button
                      type="text"
                      onClick={() => handleViewDetail(order.id)}
                      size="small"
                      key="detail"
                    >
                      查看详情
                    </Button>
                  ]}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      🛒
                    </div>
                    <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                      订单 #{order.order_no}
                    </Title>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        💰 ¥{order.total_amount}
                      </Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📅 {dayjs(order.created_at).format('MM-DD HH:mm')}
                      </Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color={getStatusColor(order.payment_status)} style={{ fontSize: '10px' }}>
                        {getStatusText(order.payment_status)}
                      </Tag>
                    </div>
                    {order.product_name && (
                      <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
                        📦 {order.product_name}
                      </Text>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* 订单详情模态框 */}
      <Modal
        title="订单详情"
        open={orderDetailVisible}
        onCancel={() => setOrderDetailVisible(false)}
        footer={null}
        width={800}
      >
        {currentOrder && (
          <div>
            <Descriptions title="订单信息" bordered column={2} size="small">
              <Descriptions.Item label="订单号">{currentOrder.order_no}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(currentOrder.payment_status)}</Descriptions.Item>
              <Descriptions.Item label="卡密类型">{getCardTypeName(currentOrder.card_type)}</Descriptions.Item>
              <Descriptions.Item label="购买数量">{currentOrder.quantity}</Descriptions.Item>
              <Descriptions.Item label="单价">¥{currentOrder.unit_price}</Descriptions.Item>
              <Descriptions.Item label="总金额">¥{currentOrder.total_amount}</Descriptions.Item>
              <Descriptions.Item label="支付方式">{getPaymentMethodName(currentOrder.payment_method)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{new Date(currentOrder.created_at).toLocaleString()}</Descriptions.Item>
              {currentOrder.payment_time && (
                <Descriptions.Item label="支付时间">{new Date(currentOrder.payment_time).toLocaleString()}</Descriptions.Item>
              )}
              {currentOrder.confirmed_time && (
                <Descriptions.Item label="确认时间">{new Date(currentOrder.confirmed_time).toLocaleString()}</Descriptions.Item>
              )}
            </Descriptions>

            {currentOrder.payment_note && (
              <Card title="支付备注" size="small" style={{ marginTop: '16px' }}>
                <p>{currentOrder.payment_note}</p>
              </Card>
            )}

            {currentOrder.payment_proof && (
              <Card title="支付凭证" size="small" style={{ marginTop: '16px' }}>
                <Image
                  src={currentOrder.payment_proof}
                  alt="支付凭证"
                  style={{ maxWidth: '200px' }}
                />
              </Card>
            )}

            {currentOrder.card_codes && (
              <Card title="生成的卡密" size="small" style={{ marginTop: '16px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  {currentOrder.card_codes.split(',').map((code, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>
                      {code}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyOrders;
